// Shared UI components. (06 §Components)
import type { Balance, Member, Settlement } from "./api";
import { money as fmt, signedMoney as fmtSigned } from "../shared/money";

export { fmt, fmtSigned };

export function MoneyAmount({ minor, ccy, signed }: { minor: number; ccy: string; signed?: boolean }) {
  const cls = minor > 0 ? "pos" : minor < 0 ? "neg" : "";
  return <span class={`money ${signed ? cls : ""}`}>{signed ? fmtSigned(minor, ccy) : fmt(minor, ccy)}</span>;
}

export function StatusPill({ status }: { status: "active" | "closed" }) {
  return <span class={`pill ${status}`}>{status === "closed" ? "Settled" : "Active"}</span>;
}

const CAT_ICON: Record<string, string> = {
  food: "🍽",
  transport: "🚕",
  lodging: "🛏",
  activities: "🎟",
  shopping: "🛍",
  other: "•",
};
export function CategoryIcon({ category }: { category: string }) {
  return <span class="cat" title={category} aria-label={category}>{CAT_ICON[category] ?? "•"}</span>;
}

export function Avatars({ members }: { members: { displayName: string }[] }) {
  return (
    <div class="avatars">
      {members.slice(0, 5).map((m) => (
        <div class="avatar" title={m.displayName}>
          {m.displayName.slice(0, 1).toUpperCase()}
        </div>
      ))}
    </div>
  );
}

export function SettlementList({
  transfers,
  ccy,
  nameOf,
  closing,
}: {
  transfers: Settlement[];
  ccy: string;
  nameOf: (id: string) => string;
  closing?: boolean;
}) {
  if (transfers.length === 0) return <p class="sub">All square — nobody owes anybody.</p>;
  return (
    <div>
      {transfers.map((t) => (
        <div class="settle">
          <span class="who">
            {nameOf(t.from)} <span class="arrow">→</span> {nameOf(t.to)}
          </span>
          <MoneyAmount minor={t.amountBaseMinor} ccy={ccy} />
        </div>
      ))}
    </div>
  );
}

/**
 * BalanceStrip — the signature element. People are nodes along a ribbon; debts
 * are directed connections flowing toward whoever is owed. Animates the flow;
 * for a closed (settled) trip it flattens to zero. Respects reduced-motion via
 * the CSS .flow-dash rule.
 */
export function BalanceStrip({
  balances,
  members,
  settled,
}: {
  balances: Balance[];
  members: Member[];
  settled: boolean;
}) {
  const nameOf = (id: string) => members.find((m) => m.userId === id)?.displayName ?? id;
  const n = balances.length;
  const W = Math.max(320, n * 120);
  const H = 120;
  const cx = (i: number) => 50 + (i * (W - 100)) / Math.max(1, n - 1);
  const cy = H / 2;
  const max = Math.max(1, ...balances.map((b) => Math.abs(b.netBaseMinor)));

  // node x positions in balance order
  const pos = new Map(balances.map((b, i) => [b.userId, cx(i)]));

  const debtors = balances.filter((b) => b.netBaseMinor < 0);
  const creditors = balances.filter((b) => b.netBaseMinor > 0);

  return (
    <div class="strip" role="img" aria-label="Balance flow between members">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
        {/* baseline rule */}
        <line x1="40" y1={cy} x2={W - 40} y2={cy} stroke="var(--rule)" stroke-width="1" />
        {/* flows debtor → creditor (skip when settled) */}
        {!settled &&
          debtors.flatMap((d) =>
            creditors.map((c) => {
              const x1 = pos.get(d.userId)!;
              const x2 = pos.get(c.userId)!;
              const lift = 34 + (Math.abs(d.netBaseMinor) / max) * 18;
              const my = cy - lift;
              return (
                <path
                  d={`M ${x1} ${cy} Q ${(x1 + x2) / 2} ${my} ${x2} ${cy}`}
                  fill="none"
                  stroke="var(--owed)"
                  stroke-width="1.5"
                  class="flow-dash"
                  opacity="0.7"
                />
              );
            }),
          )}
        {/* nodes */}
        {balances.map((b) => {
          const x = pos.get(b.userId)!;
          const owed = b.netBaseMinor > 0;
          const owes = b.netBaseMinor < 0;
          const r = 7 + (Math.abs(b.netBaseMinor) / max) * 9;
          const color = settled ? "var(--muted)" : owed ? "var(--owed)" : owes ? "var(--owes)" : "var(--muted)";
          return (
            <g>
              <circle cx={x} cy={cy} r={r} fill={color} />
              <text x={x} y={cy - r - 8} text-anchor="middle" font-size="12" fill="var(--ink)" font-family="var(--sans)">
                {nameOf(b.userId)}
              </text>
              <text x={x} y={cy + r + 16} text-anchor="middle" font-size="11" fill="var(--muted)" font-family="var(--mono)">
                {settled ? "0" : (b.netBaseMinor / 100).toFixed(0)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div class="empty">
      <div class="big">{title}</div>
      <div>{body}</div>
    </div>
  );
}
